import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles, Star } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND, STATS, MENU } from "@/lib/brand";
import { SectionEyebrow, SectionHeading, SectionWrap, FadeUp, StatCard, CtaBand } from "@/components/sections/Primitives";

export default function HomePage() {
  useSeo({
    title: "Pizza Excellente | Neighborhood Pizza Brand",
    description:
      "Discover Pizza Excellente, a neighborhood-focused pizza brand bringing authentic thin-crust pizza, gluten-free options, and franchise growth in India.",
    path: "/",
    ogImage: ASSETS.hero_pizza,
    ogImageAlt: "Pizza Excellente — neighborhood pizza brand",
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[100svh] flex items-end overflow-hidden pe-grain"
      >
        <img
          src={ASSETS.hero_pizza}
          alt="Thin-crust pizza with premium toppings from Pizza Excellente"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/35 to-black/80" />
        <div className="relative w-full max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pb-16 md:pb-24 pt-32">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-8"
            >
              <div className="flex items-center gap-4 mb-8 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                <span className="h-[2px] w-12 md:w-16 bg-pe-cream" />
                <span className="text-pe-cream font-serif italic text-2xl md:text-3xl lg:text-4xl tracking-tight">
                  Crust above the rest
                </span>
              </div>
              <h1 className="font-serif text-[44px] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[96px] text-white tracking-tight">
                Pizza made for
                <br />
                <span className="italic text-pe-rose-soft">neighborhoods.</span>
              </h1>
              <p className="mt-8 text-white/85 max-w-2xl text-base md:text-lg leading-relaxed font-light">
                Pizza Excellente is a neighborhood-focused pizza brand bringing authentic Italian-style thin-crust pizza,
                premium ingredients, gluten-free options, and a warm dine-in or takeout experience to India’s residential communities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-4 flex flex-col gap-3 md:items-end"
            >
              <a
                data-testid="hero-cta-franchise"
                href={BRAND.mailto}
                className="group inline-flex items-center gap-3 bg-pe-orange text-pe-ink px-7 py-4 text-[12px] tracking-[0.15em] font-medium uppercase hover:bg-white transition-colors w-full md:w-auto justify-between md:justify-center"
              >
                Explore Franchise <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                data-testid="hero-cta-menu"
                to="/menu-locations"
                className="inline-flex items-center gap-3 border border-white/30 text-white px-7 py-4 text-[12px] tracking-[0.15em] font-medium uppercase hover:bg-white/10 transition-colors w-full md:w-auto justify-between md:justify-center"
              >
                Menu & Locations <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8 max-w-4xl">
            {[
              { v: "50+", l: "Years of Boston heritage" },
              { v: "~700", l: "Sq ft store footprint" },
              { v: "70/30", l: "Takeout / dine-in" },
              { v: "T2 & T3", l: "Cities focus" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + i * 0.08 }}
                className="border-t border-white/25 pt-4"
              >
                <div className="font-serif text-3xl md:text-4xl text-white">{s.v}</div>
                <div className="mt-2 text-[10px] md:text-xs tracking-[0.18em] uppercase text-white/70">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 1 — Built for where people live */}
      <SectionWrap testId="home-section-built-for-where" tone="cream">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <FadeUp className="md:col-span-7 order-2 md:order-1">
            <SectionHeading className="mt-6">Built for where people live.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed max-w-xl">
              Most pizza brands chase malls and shopping areas. Pizza Excellente is built for residential neighborhoods —
              where convenience, repeat demand, and community loyalty matter most.
            </p>
            <p className="mt-5 text-pe-ink/75 text-lg leading-relaxed max-w-xl">
              The concept is designed to establish a presence within or near large apartment communities and dense local catchments,
              making fresh, high-quality pizza easily accessible close to home.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <StatCard testId="stat-residents-nearby" value="5K+" label="Residents nearby" />
              <StatCard testId="stat-radius-residents" value="25K" label="Within 1 km" accent="orange" />
              <StatCard testId="stat-delivery-radius" value="1 km" label="Free delivery" accent="rose" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-5 order-1 md:order-2">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={ASSETS.cafe_interior}
                alt="Upscale Pizza Excellente store interior concept"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-pe-cream/95 backdrop-blur p-5">
                <div className="eyebrow text-pe-maroon mb-1">Format</div>
                <p className="font-serif text-xl text-pe-ink leading-snug">A café-style counter, room for ~20 guests, kitchen built for flow.</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 2 — Authentic roots */}
      <SectionWrap testId="home-section-authentic-roots" tone="mauve">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <FadeUp className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={ASSETS.pizza_prep}
                alt="Pizza dough preparation in a warm kitchen"
                className="w-full h-full object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <SectionHeading className="mt-6">A concept with authentic roots.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              Pizza Excellente is inspired by a celebrated downtown Boston pizzeria with more than 50 years of heritage
              and a reputation for authentic, homemade Italian-style pizza.
            </p>
            <p className="mt-5 text-pe-ink/75 text-lg leading-relaxed">
              That inspiration has been reimagined into a modern QSR format designed for India — combining product quality,
              neighborhood convenience, and scalable execution.
            </p>
            <Link
              to="/our-story"
              data-testid="home-our-story-link"
              className="inline-flex items-center gap-2 mt-8 text-pe-maroon hover:text-pe-maroon-deep group text-sm tracking-[0.15em] uppercase font-medium"
            >
              Read our story <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 3 — What makes us different */}
      <SectionWrap testId="home-section-different" tone="white">
        <FadeUp>
          <div className="max-w-3xl">
            <SectionHeading className="mt-6">What makes us different.</SectionHeading>
          </div>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-px bg-pe-ink/10 mt-12 md:mt-16">
          {[
            { icon: Sparkles, title: "Authentic thin-crust", body: "Italian-style gourmet pizza prepared fresh, with care for traditional techniques and modern execution." },
            { icon: Star, title: "Premium ingredients", body: "Premium toppings, gluten-free options, and recipes engineered with our F&B consulting partners." },
            { icon: MapPin, title: "Designed for residential", body: "Neighborhood-first locations near dense apartment clusters, built for repeat local demand." },
          ].map((it, i) => (
            <FadeUp key={it.title} delay={i * 0.1}>
              <div className="bg-white p-8 md:p-10 h-full">
                <it.icon className="text-pe-maroon" size={28} strokeWidth={1.4} />
                <h3 className="font-serif text-2xl md:text-3xl text-pe-ink mt-6 leading-snug">{it.title}</h3>
                <p className="mt-4 text-pe-ink/70 leading-relaxed">{it.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </SectionWrap>

      {/* Section 4 — Growth vision */}
      <SectionWrap testId="home-section-growth" tone="ink">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <FadeUp className="md:col-span-5">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-pe-cream mt-6 leading-[1.05]">Growth vision.</h2>
            <p className="mt-8 text-pe-cream/75 text-lg leading-relaxed">
              Pizza Excellente plans to launch in <span className="text-pe-orange">Greater Noida</span> and grow through
              a mix of company-owned and franchised locations.
            </p>
            <p className="mt-5 text-pe-cream/75 text-lg leading-relaxed">
              The current plan is to open <span className="text-pe-orange font-medium">5 company-owned units within 4 years</span>
              {" "}and franchise <span className="text-pe-orange font-medium">30 additional units</span> over time.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-2 gap-px bg-white/10">
              {[
                { v: "5", l: "Company-owned units (4 yrs)" },
                { v: "30", l: "Franchised units (over time)" },
                { v: "$5.5B+", l: "India pizza market (2025)" },
                { v: "10%", l: "Sector CAGR" },
              ].map((s) => (
                <div key={s.l} className="bg-pe-ink p-7 md:p-9">
                  <div className="font-serif text-5xl md:text-6xl text-pe-orange leading-none">{s.v}</div>
                  <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-cream/70">{s.l}</div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Menu teaser marquee */}
      <SectionWrap testId="home-menu-teaser" tone="cream">
        <FadeUp className="text-center max-w-2xl mx-auto mb-12">
          <SectionEyebrow>Menu preview</SectionEyebrow>
          <SectionHeading className="mt-6">Authentic pizza, made simple.</SectionHeading>
          <p className="text-pe-ink/70 mt-6 leading-relaxed">
            Around 30 items — pizza first, supported by pasta, appetizers, sandwiches and desserts. Gluten-free crust available.
          </p>
        </FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {MENU.pizzas.map((p, i) => (
            <FadeUp key={p.name} delay={i * 0.05}>
              <div className="bg-white border border-pe-border p-5 md:p-6 h-full hover:-translate-y-1 transition-transform">
                <div className="eyebrow text-pe-rose mb-3">Pizza</div>
                <h4 className="font-serif text-xl text-pe-ink leading-tight">{p.name}</h4>
                <p className="mt-2 text-xs text-pe-ink/65 leading-relaxed">{p.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/menu-locations"
            data-testid="home-menu-cta"
            className="inline-flex items-center gap-2 border border-pe-maroon text-pe-maroon hover:bg-pe-maroon hover:text-white transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
          >
            See the full menu <ArrowRight size={16} />
          </Link>
        </div>
      </SectionWrap>

      {/* CTA band */}
      <CtaBand
        testId="home-cta-band"
        eyebrow="Let's connect"
        heading="Let's build the neighborhood pizza brand of tomorrow."
        body="Whether you're a future franchise partner, landlord, supplier, or customer — Pizza Excellente invites you to connect and learn more."
        ctaLabel="Contact Pizza Excellente"
        ctaHref={BRAND.mailto}
        secondaryLabel="See Franchise Opportunity"
        secondaryHref="/franchise-opportunity"
      />
    </div>
  );
}
